import { inject, Injectable } from '@angular/core';
import { getOrderUuid, getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import { ProductBookingService } from '@products/services/product-booking.service';
import { TariffBookingService } from '@products/services/tariff-booking.service';
import {
  getBookedProducts,
  getSharedBookedPersonIdTariffsTotalCount
} from '@store/products/booking/product/product-booking.selectors';
import { getPreferredTariffTypeWithTariff } from '@store/products/product-selection/product-selection.selectors';
import { isEmpty, isNil, isNull } from 'lodash';
import { combineLatest, first, map, of, switchMap, zip } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PreferredBookingService {
  private readonly store = inject(Store);
  private readonly productBookingService = inject(ProductBookingService);
  private readonly tariffBookingService = inject(TariffBookingService);

  setPreferredTariffBooking$(ticketTypeId: number, ticketPersonTypeId: number, count: number) {
    return combineLatest([
      this.store.select(getSelectedExhibitionId),
      this.store.select(getOrderUuid),
      this.store.select(getPreferredTariffTypeWithTariff(ticketTypeId, ticketPersonTypeId)),
      this.store.select(getBookedProducts)
    ]).pipe(
      first(
        ([eventId, orderUuid, preferredTicketTypeWithTariff]) =>
          !isNil(eventId) && !isEmpty(orderUuid) && !isNull(preferredTicketTypeWithTariff)
      ),
      map(([eventId, orderUuid, preferredTicketTypeWithTariff, bookedProductTypes]) => {
        const preferredBookingProductTypes = this.tariffBookingService.mapTariffTypeToBookingTariffType(
          preferredTicketTypeWithTariff,
          preferredTicketTypeWithTariff.tariffs[0],
          0,
          count
        );

        this.productBookingService.removeProductBookings(bookedProductTypes);

        const preferredBookingAndRemovedBookingProductTypes = [...bookedProductTypes, preferredBookingProductTypes];

        return {
          eventId,
          orderUuid,
          preferredBookingAndRemovedBookingProductTypes
        };
      }),
      switchMap(({ eventId, orderUuid, preferredBookingAndRemovedBookingProductTypes }) =>
        zip(
          of({ eventId, orderUuid, preferredBookingAndRemovedBookingProductTypes }),
          this.store.select(getSharedBookedPersonIdTariffsTotalCount(preferredBookingAndRemovedBookingProductTypes))
        ).pipe(
          map(([combinedLatest, sharedBookedPersonIdTariffsCount]) => ({
            combinedLatest,
            sharedBookedPersonIdTariffsCount
          }))
        )
      ),
      map(({ combinedLatest, sharedBookedPersonIdTariffsCount }) => {
        const { eventId, orderUuid, preferredBookingAndRemovedBookingProductTypes } = combinedLatest;

        return {
          preferredBookingAndRemovedBookingProductTypes,
          postProductBookings: this.productBookingService.mapBookingProductTypeToPostProductBooking(
            preferredBookingAndRemovedBookingProductTypes,
            eventId,
            orderUuid,
            sharedBookedPersonIdTariffsCount
          )
        };
      })
    );
  }
}
